/**
 * Map container component
 */

import React, { useEffect, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import { useAppContext } from '../../context/AppContext';
import { processCountryData } from '../../services/dataService';
import MapMarker from './MapMarker';
import { UnifiedMarker } from './PieChartMarker';
import TimezoneLayer from './TimezoneLayer';
import '../../styles/components/map/MapContainer.css';

/**
 * Map container component - Handles the Leaflet map initialization and rendering
 * @returns {JSX.Element} MapContainer component
 */
const MapContainer = ({ onCountrySelect }) => {
  // Refs for map elements
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const LRef = useRef(null);
  const containerRef = useRef(null);
  
  // Local state for timezone layer
  const [showTimezones, setShowTimezones] = useState(false);
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  const [showTimezoneNotification, setShowTimezoneNotification] = useState(false);
  
  
  // Store global maximums in refs so they're calculated once and never change
  const globalMaximumsRef = useRef({ maxTotal: 1, maxLargest: 1, calculated: false });
  
  // Get data and zoom handlers from context
  const { 
    loading, 
    error, 
    clientData,
    countryData,
    filteredCountryData,
    filteredClientData,
    searchQuery,
    zoom,
    setZoom,
    activeTierFilter,
    isLoadingClients,
    useTierColors,
    usePieChartMarkers,
    // Get the original country data that hasn't been overridden by pinned client filtering
    originalFilteredCountryData,
  } = useAppContext();

  // Set body styles on mount
  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflowX = 'hidden';
    document.body.style.width = '100vw';
    
    return () => {
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflowX = '';
      document.body.style.width = '';
    };
  }, []);

  // Initialize map once data is ready
  useEffect(() => {
    if (loading || mapRef.current) return;

    (async () => {
      try {
        const L = (await import('leaflet')).default;
        LRef.current = L;

        // Use ref for map container
        const mapContainer = containerRef.current;
        if (!mapContainer || mapRef.current) return;

        // Only initialize if container exists
        if (!mapContainer) return;

        // --- UPDATE: Map now initializes with zoom level 3 ---
        const map = L.map(mapContainer, { 
          center: [20, 0], 
          zoom: 3, 
          zoomControl: false, 
          attributionControl: false,
          minZoom: 2,  // Set minimum zoom level to 2
          maxZoom: 5   // Set maximum zoom level to 5
        });

        mapRef.current = map;
        layerRef.current = L.layerGroup().addTo(map);
        setZoom(map.getZoom());

        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { 
          attribution: '', 
          maxZoom: 5 
        }).addTo(map);

        // Handle zoom events with limitation
        map.on('zoomend', () => {
          const currentZoom = map.getZoom();
          // Ensure zoom doesn't go below level 2
          if (currentZoom < 2) {
            map.setZoom(2);
            return;
          }
          setZoom(currentZoom);
        });

        // Prevent zoom out below level 2 during zoom operations
        map.on('zoom', () => {
          const currentZoom = map.getZoom();
          if (currentZoom < 2) {
            map.setZoom(2);
          }
        });
      } catch (err) {
        console.error('Error initializing map:', err);
      }
    })();
    
    return () => { 
      if (mapRef.current) { 
        // Remove all event listeners and destroy the map instance
        mapRef.current.off();
        mapRef.current.remove();
        mapRef.current = null; 
      } 
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Show popup immediately when map is ready and no pinned clients exist
  useEffect(() => {
    // Use a small delay to ensure map is fully initialized
    const timer = setTimeout(() => {
      const map = mapRef.current;
      
      if (!map || loading) return;

      const mapContainer = map.getContainer();
      let messageOverlay = mapContainer.querySelector('.map-sync-message');
      let backgroundOverlay = mapContainer.querySelector('.map-background-overlay');

      // Check if we should show the popup immediately (no pinned clients and not loading)
      if (!filteredCountryData.length && !isLoadingClients) {
        if (!backgroundOverlay) {
          backgroundOverlay = document.createElement('div');
          backgroundOverlay.className = 'map-background-overlay';
          backgroundOverlay.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.15);
            z-index: 999;
            pointer-events: none;
          `;
          mapContainer.appendChild(backgroundOverlay);
        }
        
        if (!messageOverlay) {
          messageOverlay = document.createElement('div');
          messageOverlay.className = 'map-sync-message';
          messageOverlay.style.cssText = `
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(255, 255, 255, 0.95);
            padding: 24px;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
            text-align: center;
            z-index: 1001;
            max-width: 320px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            backdrop-filter: blur(8px);
            border: 1px solid rgba(255, 255, 255, 0.2);
          `;
          mapContainer.appendChild(messageOverlay);
          
          messageOverlay.innerHTML = `
            <div style="color: #666; font-size: 14px; line-height: 1.4;">
              <div style="font-weight: 600; margin-bottom: 8px;">Map Synchronized with Pinned List</div>
              <div>Pin some clients to see them on the map</div>
            </div>
          `;
        }
      } else if (filteredCountryData.length > 0) {
        // Add a small delay before removing popup when clients are pinned to allow markers to render first
        setTimeout(() => {
          const currentMessageOverlay = mapContainer.querySelector('.map-sync-message');
          const currentBackgroundOverlay = mapContainer.querySelector('.map-background-overlay');
          if (currentMessageOverlay) {
            currentMessageOverlay.remove();
          }
          if (currentBackgroundOverlay) {
            currentBackgroundOverlay.remove();
          }
        }, 1500); // Delay to ensure markers are fully rendered
      } else {
        // Remove popup and overlay immediately when loading
        if (messageOverlay) {
          messageOverlay.remove();
        }
        if (backgroundOverlay) {
          backgroundOverlay.remove();
        }
      }
    }, 100); // Small delay to ensure map is initialized

    return () => clearTimeout(timer);
  }, [loading, filteredCountryData.length, isLoadingClients]);

  // Draw markers & popups
  useEffect(() => {
    const map = mapRef.current, 
          layer = layerRef.current, 
          L = LRef.current;
          
    if (!map || !layer || loading) return;
    
    layer.clearLayers();
    
    // Show loading state or message when no pinned clients are available
    if (!filteredCountryData.length || isLoadingClients) {
      // Clear existing markers
      layer.clearLayers();
      
      // Add a message overlay to indicate map is synchronized with pinned list
      const mapContainer = map.getContainer();
      let messageOverlay = mapContainer.querySelector('.map-sync-message');
      let backgroundOverlay = mapContainer.querySelector('.map-background-overlay');
      
      if (!backgroundOverlay) {
        backgroundOverlay = document.createElement('div');
        backgroundOverlay.className = 'map-background-overlay';
        backgroundOverlay.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.15);
          z-index: 999;
          pointer-events: none;
        `;
        mapContainer.appendChild(backgroundOverlay);
      }
      
      if (!messageOverlay) {
        messageOverlay = document.createElement('div');
        messageOverlay.className = 'map-sync-message';
        messageOverlay.style.cssText = `
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(255, 255, 255, 0.95);
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          text-align: center;
          z-index: 1001;
          max-width: 320px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        `;
        mapContainer.appendChild(messageOverlay);
      }
      
      if (isLoadingClients) {
        messageOverlay.innerHTML = `
          <div style="color: #666; font-size: 14px; line-height: 1.4;">
            <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 12px;">
              <div style="
                width: 20px;
                height: 20px;
                border: 2px solid #f3f3f3;
                border-top: 2px solid #0074D9;
                border-radius: 50%;
                animation: spin 1s linear infinite;
                margin-right: 8px;
              "></div>
              <div style="font-weight: 600;">Loading clients...</div>
            </div>
            <div>Please wait while we load the selected clients</div>
          </div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        `;
      } else {
        messageOverlay.innerHTML = `
          <div style="color: #666; font-size: 14px; line-height: 1.4;">
            <div style="font-weight: 600; margin-bottom: 8px;">Map Synchronized with Pinned List</div>
            <div>Pin some clients to see them on the map</div>
          </div>
        `;
      }
      
      return;
    }
    
    // Remove message overlay and background overlay if they exist (with delay to match marker rendering)
    const mapContainer = map.getContainer();
    setTimeout(() => {
      const messageOverlay = mapContainer.querySelector('.map-sync-message');
      const backgroundOverlay = mapContainer.querySelector('.map-background-overlay');
      if (messageOverlay) {
        messageOverlay.remove();
      }
      if (backgroundOverlay) {
        backgroundOverlay.remove();
      }
    }, 1500); // Delay to ensure markers are fully rendered

    // Use global maximum values from the complete, unfiltered dataset to preserve circle sizes during filtering
    // This ensures circles maintain consistent sizes regardless of tier filters, manager selection, or other filtering
    
    // Try multiple sources to find the true "All Managers" data
    let sourceData = null;
    let sourceName = '';
    
    if (originalFilteredCountryData && originalFilteredCountryData.length > 0) {
      sourceData = originalFilteredCountryData;
      sourceName = 'originalFilteredCountryData';
    } else if (countryData && countryData.length > 0) {
      sourceData = countryData;
      sourceName = 'countryData';
    } else if (clientData && clientData.length > 0) {
      const { countryData: calculatedCountryData } = processCountryData(clientData);
      sourceData = calculatedCountryData;
      sourceName = 'calculated from clientData';
    }

    // Calculate global maximums once from the original country data that hasn't been filtered by manager selection
    // This ensures circles always have the same size as when "All Managers" is selected
    if (!globalMaximumsRef.current.calculated && sourceData && sourceData.length > 0) {
      globalMaximumsRef.current.maxTotal = Math.max(...sourceData.map(d => d.total));
      globalMaximumsRef.current.maxLargest = Math.max(...sourceData.map(d => d.largest));
      globalMaximumsRef.current.calculated = true;
      
      // Debug logging to verify we're getting "All Managers" values
      console.log('Global max values calculated from', sourceName, ':', {
        maxTotal: globalMaximumsRef.current.maxTotal,
        maxLargest: globalMaximumsRef.current.maxLargest,
        totalCountries: sourceData.length,
        filteredCountries: filteredCountryData.length,
        source: sourceName
      });
    }
    
    const globalMaxTotal = globalMaximumsRef.current.maxTotal;
    const globalMaxLargest = globalMaximumsRef.current.maxLargest;

    filteredCountryData.forEach(country => {
      // Find the original country data (All Managers) for this country to get correct total/largest values
      let originalCountry = country;
      if (sourceData) {
        const foundOriginal = sourceData.find(c => c.country === country.country);
        if (foundOriginal) {
          // Use original total/largest values but keep filtered country's other properties
          originalCountry = {
            ...country,
            total: foundOriginal.total,
            largest: foundOriginal.largest
          };
        }
      }
      
      // Choose marker type based on usePieChartMarkers setting
      let marker;
      if (usePieChartMarkers) {
        // Use new unified marker (handles both single and multi-tier with SVG)
        marker = new UnifiedMarker({
          country: originalCountry,
          maxTotal: globalMaxTotal,
          maxLargest: globalMaxLargest,
          zoom,
          L,
          layer,
          activeTierFilter,
          showTimezones,
          clientData: filteredClientData,
          useTierColors,
          onSelect: () => {
            onCountrySelect(country);
            map.panTo([country.latitude, country.longitude]);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      } else {
        // Use original circle marker design
        marker = new MapMarker({
          country: originalCountry,
          maxTotal: globalMaxTotal,
          maxLargest: globalMaxLargest,
          zoom,
          L,
          layer,
          activeTierFilter,
          showTimezones,
          clientData: filteredClientData,
          useTierColors,
          onSelect: () => {
            onCountrySelect(country);
            map.panTo([country.latitude, country.longitude]);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      }
      
      marker.addToMap();
    });
  }, [filteredCountryData, countryData, clientData, zoom, loading, searchQuery, onCountrySelect, activeTierFilter, showTimezones, filteredClientData, isLoadingClients, useTierColors, usePieChartMarkers]);

  return (
    <>
      <div ref={containerRef} className="map" />
      
      
      {/* Timezone Layer */}
      <TimezoneLayer
        map={mapRef.current}
        L={LRef.current}
        visible={showTimezones}
        onLoadStateChange={setTimezoneLoading}
      />
      
      {/* Map Controls Overlay */}
      <div className="map-controls">
        <div className={`timezone-toggle-container ${showTimezoneNotification ? 'extended' : ''}`}>
          <button
            className={`timezone-toggle ${showTimezones ? 'active' : ''}`}
            onClick={() => {
              const newShowState = !showTimezones;
              setShowTimezones(newShowState);
              
              // Show notification about timezone changes when zones are first displayed
              if (newShowState) {
                setShowTimezoneNotification(true);
                console.info('⚠️ Timezone Display Notice: Time zones shown reflect current time including daylight saving adjustments. Some regions may show different offsets during standard time periods.');
              } else {
                setShowTimezoneNotification(false);
              }
            }}
            disabled={timezoneLoading}
            title={showTimezones ? 'Hide Timezones' : 'Show Timezones'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12,6 12,12 16,14"/>
            </svg>
            {timezoneLoading ? 'Loading...' : (showTimezones ? 'Hide Zones' : 'Show Zones')}
          </button>
          {showTimezoneNotification && (
            <div className="timezone-notification">
              Note: Timezones reflect current time including daylight saving adjustments
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MapContainer;
